import {Injectable, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FeatureService} from 'src/app/services/api/feature.service';
import {FeatureFlag, FeatureFlagsResponse} from 'src/app/models/feature-flag.model';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs';
import {FeatureFlags} from 'src/app/models/constants/feature-flags.constant';
import {TranslocoService} from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsManagerService {
  private featureService = inject(FeatureService);
  private destroyRef = inject(DestroyRef);
  private transloco = inject(TranslocoService);

  private tenantFeatureFlagsSubject = new BehaviorSubject<FeatureFlag[]>([]);
  private instanceFeatureFlagsSubject = new BehaviorSubject<FeatureFlag[]>([]);
  private currentContextSubject = new BehaviorSubject<{type: 'tenant' | 'instance'; id: string} | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private rawResponseSubject = new BehaviorSubject<FeatureFlagsResponse | null>(null);
  private instanceFeatureFlagsMap: { [key: string]: FeatureFlag[] } = {};
  private instanceFlagPromises: { [key: string]: Promise<FeatureFlag[]> } = {};

  tenantFeatureFlags$ = this.tenantFeatureFlagsSubject.asObservable();
  instanceFeatureFlags$ = this.instanceFeatureFlagsSubject.asObservable();
  currentContext$ = this.currentContextSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();
  error$ = this.errorSubject.asObservable();
  rawResponse$ = this.rawResponseSubject.asObservable();

  // Backward compatibility
  featureFlags$ = this.tenantFeatureFlags$;
  currentTenant$ = this.currentContextSubject.pipe(
    map(context => context?.type === 'tenant' ? context.id : null)
  );

  get featureFlags(): FeatureFlag[] {
    return this.tenantFeatureFlagsSubject.value;
  }

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  get rawResponse(): FeatureFlagsResponse | null {
    return this.rawResponseSubject.value;
  }

  get currentContext(): {type: 'tenant' | 'instance'; id: string} | null {
    return this.currentContextSubject.value;
  }

  // Backward compatibility
  get currentTenant(): string | null {
    const context = this.currentContextSubject.value;
    return context?.type === 'tenant' ? context.id : null;
  }

  loadFeatureFlags(tenantId: string): void {
    if (!tenantId) {
      return;
    }

    this.setLoading(true);
    this.clearError();
    this.currentContextSubject.next({type: 'tenant', id: tenantId});

    this.featureService.getTenantIdFeatureFlags(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: FeatureFlagsResponse) => {
          const flags = response.featureFlags || [];
          this.tenantFeatureFlagsSubject.next(flags);
          this.rawResponseSubject.next(response);
          this.setLoading(false);
        },
        error: (_error: unknown) => {
          this.setLoading(false);
          const errorMsg = this.transloco.translate('FEATURE_FLAG.ERROR_LOAD');
          this.errorSubject.next(errorMsg);
        }
      });
  }

  loadInstanceFeatureFlags(instanceId: string): void {
    if (!instanceId) {
      return;
    }

    this.setLoading(true);
    this.clearError();
    this.currentContextSubject.next({type: 'instance', id: instanceId});

    this.featureService.getInstanceFeatureFlags(instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: FeatureFlagsResponse) => {
          const flags = response.featureFlags || [];
          this.instanceFeatureFlagsSubject.next(flags);
          this.rawResponseSubject.next(response);
          this.setLoading(false);
        },
        error: (_error: unknown) => {
          this.setLoading(false);
          const errorMsg = this.transloco.translate('FEATURE_FLAG.ERROR_LOAD');
          this.errorSubject.next(errorMsg);
        }
      });
  }

  getFeatureFlag(id: string): FeatureFlag | undefined {
    return this.featureFlags.find(f => f.id === id);
  }

  isFeatureEnabled(id: string | FeatureFlags): boolean {
    const flag = this.getFeatureFlag(id);
    if (!flag) return false;

    const enabled = flag.enabled as any;

    // Handle string responses from API ("true" / "false")
    if (typeof enabled === 'string') {
      return enabled.toLowerCase() === 'true';
    }

    return Boolean(enabled);
  }

  isFeatureEnabledTypeSafe(id: FeatureFlags): boolean {
    return this.isFeatureEnabled(id);
  }

  getEnabledFlagIds(): string[] {
    return this.featureFlags.filter(f => f.enabled).map(f => f.id);
  }

  getDisabledFlagIds(): string[] {
    return this.featureFlags.filter(f => !f.enabled).map(f => f.id);
  }

  getAllFlagIds(): string[] {
    return this.featureFlags.map(f => f.id);
  }

  hasFeatureFlags(): boolean {
    return this.featureFlags.length > 0;
  }

  getFeatureFlagStatus(id: string): {id: string; enabled: boolean} | undefined {
    const flag = this.getFeatureFlag(id);
    if (flag) {
      const isEnabled = flag.enabled === true || flag.enabled === 'true';
      return {id: flag.id, enabled: isEnabled};
    }
    return undefined;
  }

  private setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }

  private clearError(): void {
    this.errorSubject.next(null);
  }

  clear(): void {
    this.tenantFeatureFlagsSubject.next([]);
    this.instanceFeatureFlagsSubject.next([]);
    this.rawResponseSubject.next(null);
    this.currentContextSubject.next(null);
    this.clearError();
  }

    isInstanceFeatureEnabled(instanceId: string, featureId: string | FeatureFlags): boolean {
    if (!instanceId) {
      return false;
    }

    // Check if flags are loaded for this instance
    const loadedFlags = this.instanceFeatureFlagsMap[instanceId];
    if (!loadedFlags) {
      return false; // Return false while loading
    }

    // Check if feature is enabled
    const flag = loadedFlags.find(f => f.id === featureId);
    if (!flag) {
      return false;
    }

    const enabled = flag.enabled as any;
    if (typeof enabled === 'string') {
      return enabled.toLowerCase() === 'true';
    }
    return Boolean(enabled);
  }

  loadAndStoreInstanceFeatureFlags(instanceId: string): Promise<FeatureFlag[]> {
    if (!instanceId) {
      return Promise.reject(new Error('Invalid instance ID'));
    }

    // Return cached flags if already loaded
    if (this.instanceFeatureFlagsMap[instanceId]) {
      return Promise.resolve(this.instanceFeatureFlagsMap[instanceId]);
    }

    // Return existing promise if already loading to prevent duplicate API calls
    if (!this.instanceFlagPromises[instanceId]) {
      this.instanceFlagPromises[instanceId] = new Promise((resolve, reject) => {
        this.featureService.getInstanceFeatureFlags(instanceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: FeatureFlagsResponse) => {
              const flags = response.featureFlags || [];
              this.instanceFeatureFlagsMap[instanceId] = flags;
              delete this.instanceFlagPromises[instanceId];
              resolve(flags);
            },
            error: (error: unknown) => {
              delete this.instanceFlagPromises[instanceId];
              reject(error);
            }
          });
      });
    }

    return this.instanceFlagPromises[instanceId];
  }

  getInstanceFeatureFlags(instanceId: string): FeatureFlag[] {
    return this.instanceFeatureFlagsMap[instanceId] || [];
  }

  getAllFeatureByInstanceid(instanceId: string): Observable<FeatureFlag[]> {
    this.loadInstanceFeatureFlags(instanceId);
    return this.instanceFeatureFlags$;
  }
}
